import React, { useEffect, useState, useMemo } from 'react';
import apis from '../../../../apis';
import { message, Empty } from '@drip/drip-design';
import './index.less';
import utils from '@/common/utils';
import { Chart, Axis, Tooltip, Interval, Interaction, Coordinate, LineAdvance } from 'bizcharts';
import IconTip from '@/components/icon-tip';

export default function (props) {
  const [data, setData] = useState({});
  useEffect(() => {
    if (!props.conditions.planCode || props.conditions.realTimeData) return;
    apis.queryUGInfoList({ ...props.conditions }).then((res) => {
      const { ret, data, msg } = res;
      if (ret === '0') {
        setData(data);
      } else {
        message.error(msg);
        setData({});
      }
    });
  }, [props.conditions]);

  const scale = {
    rate: {
      formatter: (val) => {
        return utils.formatPriceWithPercent(val * 100) + '%';
      },
      alias: '比例',
    },
  };

  const getChart = (data) => {
    return (
      <Chart data={data} height={300} autoFit padding={[15, 0, 25, 0]} scale={scale}>
        <Coordinate type="theta" radius={0.8} innerRadius={0.75} />
        <Axis visible={false} />
        <Tooltip />
        <Interval
          adjust="stack"
          position="rate"
          color="name"
          label={[
            'name',
            (item) => {
              return {
                offset: 20,
                content: (data) => {
                  return `${data.name} ${utils.formatPriceWithPercent(data.rate * 100)}%`;
                },
              };
            },
          ]}
        />
        <Interaction type="element-single-selected" />
      </Chart>
    );
  };

  const getBarChart = (data) => {
    return (
      <Chart data={data} height={300} autoFit theme={{ maxColumnWidth: 10 }} scale={scale}>
        <Coordinate transpose scale={[1, -1]} />
        <Axis
          name="name"
          label={{
            autoHide: false,
            autoRotate: false,
          }}
        />
        <Axis
          name="rate"
          grid={{
            line: {
              type: 'line',
              style: {
                stroke: '#ccc',
                lineDash: [3, 3],
              },
            },
          }}
          label={null}
        />
        <Tooltip />
        <Interval position="name*rate" />
      </Chart>
    );
  };

  const getLineChart = (data) => {
    return (
      <Chart autoFit height={300} data={data} scale={scale} padding={'auto'} appendPadding={10}>
        <LineAdvance
          shape="smooth"
          point
          position="name*rate"
          adjust={[
            {
              type: 'dodge',
              marginRatio: 0.1,
              rotate: 2,
            },
          ]}
        />
        <Axis name="name" label={{ rotate: 0.25, autoRotate: true, style: { textAlign: 'start', fontSize: 10 } }} />
      </Chart>
    );
  };

  const ugdata = useMemo(() => {
    let maxIndex,
      max = 0;
    data.cgpCyclLifecycle &&
      data.cgpCyclLifecycle.map((item, index) => {
        if (item.num > max) {
          max = item.num;
          maxIndex = index;
        }
      });
    return (
      <>
        <div className="user-analysis-user-ug-card">
          <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ flex: '1' }}>
              {data.dcGroupType && (
                <>
                  <div className="user-analysis-user-ug-title">
                    十大靶向人群：【{data.dcGroupType[0].name}】占主导地位
                  </div>
                  {getChart(data.dcGroupType)}
                </>
              )}
            </div>
            <div style={{ flex: '1', paddingRight: '40px' }}>
              {data.cfvSensPromotion && (
                <>
                  <div className="user-analysis-user-ug-title">
                    促销敏感度：用户对促销信息【{data.cfvSensPromotion[0].name}】
                    <IconTip
                      title="根据优惠订单占比、每单优惠金额占比的平均值、优惠金额这三个指标进行归类而得。业务方在进行促销活动时，可根据营销的目的选择不同的促销敏感度人群，方便更精准、精细化的运营"
                      type="question"
                      size="small"
                    />
                  </div>
                  {getBarChart(data.cfvSensPromotion)}
                </>
              )}
            </div>
            <div style={{ flex: '1' }}>
              {data.cvlGlobLoyalty && (
                <>
                  <div className="user-analysis-user-ug-title">
                    忠诚度：用户的购买忠诚度多为【{data.cvlGlobLoyalty[0].name}】
                    <IconTip
                      title={
                        <ul style={{ paddingLeft: 20, paddingTop: 10, paddingRight: 6 }}>
                          <li>高度-忠诚型：购物天数、购买sku单数、购买三级品类数明显偏高；</li>
                          <li>中度-忠诚型：购物天数、购买sku单数、购买三级品类数较高；</li>
                          <li>
                            近期-普通型：末单距今天数较近，购物天数、购买sku单数、购买三级品类数均低于中度-忠诚型；
                          </li>
                          <li>
                            远期-普通型：末单距今天数较远，购物天数、购买sku单数、购买三级品类数均低于近期-普通型；
                          </li>
                          <li>近期-偶然型：末单距今天数较近，折扣率低；</li>
                          <li>近期-投机型：末单距今天数较近，折扣率高；</li>
                          <li>远期-偶然型：末单距今天数较远，折扣率低；</li>
                          <li>远期-投机型：末单距今天数较远，折扣率高</li>
                        </ul>
                      }
                      type="question"
                      size="small"
                    />
                  </div>
                  {getBarChart(data.cvlGlobLoyalty)}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="user-analysis-user-ug-card">
          <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ flex: '2', paddingRight: '40px' }}>
              {data.cgpCyclLifecycle && (
                <>
                  <div className="user-analysis-user-ug-title">
                    生命周期：用户处于生命周期中【{data.cgpCyclLifecycle[maxIndex].name}】的居多
                    <IconTip
                      title="客户群体特征如下：
                      1-潜在用户-新人专区，采取措施减少用户观望时间，完成首单；
                      2-形成阶段&amp; 适应阶段-客户成长阶段，采取相应措施优化用户体验，打好用户购物基础，如问卷调查，优惠让利活动；
                      3-成长&amp; 稳定-客户维护阶段，提高用户粘性，比如优惠券营销，京东服务推荐；
                      4-衰退-客户挽留阶段，设置问卷调查，了解用户需求；
                      5-沉睡&amp; 流失-客户唤醒阶段，通过短信、邮件或推送等方式拉回用户"
                      type="question"
                      size="small"
                    />
                  </div>
                  {getLineChart(data.cgpCyclLifecycle)}
                </>
              )}
            </div>
            <div style={{ flex: '1' }}>
              {data.cvlRfmAllGroup && (
                <>
                  <div className="user-analysis-user-ug-title">
                    用户价值：按照RFM类型【{data.cvlRfmAllGroup[0].name}】居多
                    <IconTip
                      title={
                        <ul style={{ paddingLeft: 20, paddingTop: 10, paddingRight: 6 }}>
                          <li>重要价值客户:优惠后金额&gt;总体均值，订单间隔&gt;总体均值，父销售订单量&gt;总体均值</li>
                          <li>重要发展客户:优惠后金额&gt;总体均值，订单间隔&gt;总体均值，父销售订单量≤总体均值</li>
                          <li>重要保持客户:优惠后金额&gt;总体均值，订单间隔≤总体均值，父销售订单量&gt;总体均值</li>
                          <li>重要挽留客户:优惠后金额&gt;总体均值，订单间隔≤总体均值，父销售订单量≤总体均值</li>
                          <li>一般价值客户:优惠后金额≤总体均值，订单间隔&gt;总体均值，父销售订单量&gt;总体均值</li>
                          <li>一般发展客户:优惠后金额≤总体均值，订单间隔&gt;总体均值，父销售订单量≤总体均值</li>
                          <li>一般保持客户:优惠后金额≤总体均值，订单间隔≤总体均值，父销售订单量&gt;总体均值</li>
                          <li>一般挽留客户:优惠后金额≤总体均值，订单间隔≤总体均值，父销售订单量≤总体均值</li>
                        </ul>
                      }
                      type="question"
                      size="small"
                    />
                  </div>
                  {getBarChart(data.cvlRfmAllGroup)}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div>
      {Object.keys(data).length !== 0 ? (
        <>{ugdata}</>
      ) : (
        <div className="user-analysis-user-shopping-card">
          <Empty description="暂无数据" />
        </div>
      )}
    </div>
  );
}
